import React, { useEffect, useState } from "react";
import { Globe2, ListChecks, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { api, errMsg } from "../lib/api";

/**
 * Phase 126 — one independent "who receives real messages" control for ONE audience.
 *
 * audience "citizen": property-owner alerts, list holds mobile numbers, gates SMS / WhatsApp
 *                     (settings.live_allow_list / live_allow_list_saved — legacy names kept for compatibility)
 * audience "staff":   users & staff messages, list holds mobiles AND emails, gates SMS / WhatsApp / email
 *                     (settings.staff_allow_list / staff_allow_list_saved)
 *
 * Each card owns its textarea, its Save action and its own confirmation dialog. Switching one audience never
 * touches the other — the backend endpoints take `{ audience }`.
 */
export const AUDIENCE_META = {
  citizen: {
    title: "Citizens",
    listKey: "live_allow_list",
    savedKey: "live_allow_list_saved",
    what: "Property-owner messages: new demand, payment received, partial payment, due reminders, visit updates and Citizen Portal receipts.",
    allowTitle: "Test allow-list",
    allowDesc: "Only the numbers below get real SMS / WhatsApp; every other citizen is logged as “suppressed” (no charge).",
    publicTitle: "All citizens (Go public)",
    publicDesc: "Every demand, payment, reminder, visit and Citizen Portal alert is sent to the mobile number on the property record. Gateway charges apply.",
    publicBadge: "ALL citizens",
    unit: "number",
    placeholder: "+919876543210\n9123456789",
    listHelp: "Mobile numbers, one per line or comma-separated. 10-digit Indian numbers are treated as +91.",
    dialogAllTitle: "Send real messages to ALL citizens?",
    dialogAllBody: "From that moment every SMS / WhatsApp goes to the real citizen mobile on the property record and is charged by the gateway.",
    toastAll: "Citizen messages are now open to ALL citizens",
    toastAllDesc: "Every SMS / WhatsApp now goes to the mobile number on the property record.",
  },
  staff: {
    title: "Users & Staff",
    listKey: "staff_allow_list",
    savedKey: "staff_allow_list_saved",
    what: "Messages to municipal users & staff: forgot-password reset links and one-time codes — plus idle / GPS, visit and target alerts whenever they are delivered by SMS, WhatsApp or email.",
    allowTitle: "Test allow-list",
    allowDesc: "Only the mobiles and email addresses below get real SMS / WhatsApp / email; every other user is logged as “suppressed” (no charge).",
    publicTitle: "All users & staff (Go public)",
    publicDesc: "Every forgot-password link, one-time code and staff alert goes to the real mobile number / email address on the user record. Gateway charges apply.",
    publicBadge: "ALL users & staff",
    unit: "recipient",
    placeholder: "+919876543210\nname@municipality.gov.in",
    listHelp: "Mobile numbers or email addresses, one per line or comma-separated. 10-digit Indian numbers are treated as +91.",
    dialogAllTitle: "Send real messages to ALL users & staff?",
    dialogAllBody: "From that moment every forgot-password link, one-time code and staff alert goes to the real mobile / email on the user record and is charged by the gateway.",
    toastAll: "Staff messages are now open to ALL users & staff",
    toastAllDesc: "Every reset link, one-time code and staff alert now goes to the real mobile / email on the user record.",
  },
};

const plural = (n, unit) => `${n} ${unit}${n === 1 ? "" : "s"}`;

export const RecipientScopeCard = ({ audience, icon: Icon, settings, onChanged }) => {
  const meta = AUDIENCE_META[audience];
  const list = settings?.[meta.listKey] || [];
  const saved = settings?.[meta.savedKey] || [];
  const scopeAll = list.length === 0;

  const [text, setText] = useState(list.join("\n"));
  const [dialog, setDialog] = useState(null); // "all" | "allow_list" | null
  const [busy, setBusy] = useState(false);

  // keep the box in sync when the server list changes (save / restore / go public / initial load)
  useEffect(() => { setText(list.join("\n")); }, [list.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  const typedCount = text.split(/[\n,]/).map((x) => x.trim()).filter(Boolean).length;
  const tid = (s) => `${s}-${audience}`;

  const finish = (data, title, description) => {
    onChanged?.(data);
    toast.success(title, { description });
    setDialog(null);
  };

  const goPublic = async () => {
    setBusy(true);
    try {
      const r = await api.post("/platform/notification-settings/go-public", { audience });
      finish(r.data, meta.toastAll, meta.toastAllDesc);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const restrict = async () => {
    setBusy(true);
    try {
      const r = await api.post("/platform/notification-settings/restore-allow-list", { audience, numbers: text });
      const n = (r.data[meta.listKey] || []).length;
      finish(r.data, `${meta.title}: restricted to the test allow-list`, `${plural(n, meta.unit)} can receive real ${meta.title.toLowerCase()} messages.`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  // "Save list" = restrict to what is typed (audited); an EMPTY box means "go public", which needs confirmation
  const saveList = () => (typedCount === 0 ? setDialog("all") : restrict());
  const dirty = text.split(/[\n,]/).map((x) => x.trim()).filter(Boolean).join("|") !== list.join("|");

  return (
    <Card className="shadow-sm h-full" data-testid={tid("recipient-scope-card")}>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-sm font-semibold flex items-center gap-2" data-testid={tid("recipient-scope-title")}>
              {Icon && <Icon className="h-4 w-4 text-primary" />} {meta.title}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{meta.what}</p>
          </div>
          <Badge variant="outline" className={scopeAll ? "border-destructive/40 text-destructive" : ""} data-testid={tid("live-allow-list-count")}>
            {scopeAll ? meta.publicBadge : `${plural(list.length, "allowed " + meta.unit)}`}
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-3" role="radiogroup" aria-label={`${meta.title} recipient scope`}>
          <button type="button" role="radio" aria-checked={!scopeAll} data-testid={tid("scope-allow-list")}
            onClick={() => { if (scopeAll) setDialog("allow_list"); }}
            className={`text-left rounded-lg border p-3 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${!scopeAll ? "border-primary bg-primary/5" : "hover:bg-accent"}`}>
            <p className="text-sm font-medium flex items-center gap-2"><ListChecks className="h-4 w-4" /> {meta.allowTitle} {!scopeAll && <Badge className="ml-1">Active</Badge>}</p>
            <p className="text-xs text-muted-foreground mt-1">{meta.allowDesc}</p>
            {scopeAll && saved.length > 0 && <p className="text-xs text-primary mt-1">Click to restore the saved list ({plural(saved.length, meta.unit)})</p>}
          </button>
          <button type="button" role="radio" aria-checked={scopeAll} data-testid={tid("scope-all")}
            onClick={() => { if (!scopeAll) setDialog("all"); }}
            className={`text-left rounded-lg border p-3 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${scopeAll ? "border-destructive/60 bg-destructive/5" : "hover:bg-accent"}`}>
            <p className="text-sm font-medium flex items-center gap-2"><Globe2 className="h-4 w-4" /> {meta.publicTitle} {scopeAll && <Badge variant="destructive" className="ml-1">Active</Badge>}</p>
            <p className="text-xs text-muted-foreground mt-1">{meta.publicDesc}</p>
          </button>
        </div>

        <div className="space-y-2 pt-1" data-testid={tid("live-allow-list-card")}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium">Allow-list {audience === "staff" ? "numbers & emails" : "numbers"}</p>
            <Button size="sm" variant="outline" onClick={saveList} disabled={busy || !dirty} data-testid={tid("save-allow-list")}>
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} Save list
            </Button>
          </div>
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={meta.placeholder}
            className="font-mono text-sm" data-testid={tid("live-allow-list-input")} />
          <p className="text-xs text-muted-foreground">{meta.listHelp} Saving with entries restricts sending to them; “Go public” clears the list but remembers it.</p>
        </div>
      </CardContent>

      <AlertDialog open={!!dialog} onOpenChange={(o) => {
        if (o || busy) return;
        setDialog(null);
        if (typedCount === 0) setText(list.join("\n")); // cancelled "go public" from an emptied box: put the saved list back
      }}>
        <AlertDialogContent data-testid={tid("scope-dialog")}>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialog === "all" ? meta.dialogAllTitle : `Restrict ${meta.title.toLowerCase()} messages to the test allow-list?`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {dialog === "all" ? (
                  <>
                    <p>The {meta.title.toLowerCase()} allow-list ({plural(list.length, meta.unit)}) will be cleared and remembered for one-click restore. {meta.dialogAllBody}</p>
                    <p className="text-xs">This switch affects {meta.title.toLowerCase()} messages only — the other audience keeps its own setting. Make sure the Twilio balance is sufficient and Geo permissions for India are enabled.</p>
                  </>
                ) : (
                  <p>Only {typedCount > 0 ? `the ${plural(typedCount, meta.unit)} in the box` : `the ${plural(saved.length, "saved " + meta.unit)}`} will receive real {meta.title.toLowerCase()} messages; everyone else will be logged as “suppressed”.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid={tid("scope-dialog-cancel")}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); dialog === "all" ? goPublic() : restrict(); }} disabled={busy}
              className={dialog === "all" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""} data-testid={tid("scope-dialog-confirm")}>
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : dialog === "all" ? <Globe2 className="h-4 w-4 mr-1.5" /> : <ListChecks className="h-4 w-4 mr-1.5" />}
              {dialog === "all" ? "Yes, go public" : "Restrict to allow-list"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
